import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, RefreshCw, ChevronLeft, Search, CalendarDays, PhoneOutgoing, PhoneOff, PhoneCall, Bell, Settings } from 'lucide-react';
import {
    fetchAppointments,
    fetchDaysmartEmployees,
    fetchClientNotes,
    searchClients,
    fetchTicketDetails,
    closeTicket,
    getUpcomingAppointments,
    getPreviousAppointments,
    getLocationPhone,
    getAppointmentPatterns,
    DAYSMART_LOCATIONS,
    CLIENT_SEARCH_TYPES,
    groupAppointmentsByEmployee,
} from '../services/daysmartService';
import LoadingSpinner from '../LoadingSpinner';
import useUserStore from '../stores/userStore';
import { hasPermission } from '../utils/permissions';
import AppointmentDetailsModal from '../components/modals/AppointmentDetailsModal';
import GroomerAvailabilityModal from '../components/modals/GroomerAvailabilityModal';
import SchedulingClientCard from '../components/SchedulingClientCard';
import AvailabilityPanel from '../components/AvailabilityPanel';
import {
    initializeTwilioDevice,
    makeCall,
    hangupCall,
    formatPhoneNumber,
    triggerBatchGroombotReminderCalls,
    triggerGroombotReminderCall,
    fetchReminderChains,
    fetchClientCommunicationChains,
    recordCommunicationEvent,
    COMMUNICATION_EVENT_TYPES,
    getEventTypeInfo,
} from '../services/communicationCenterService';

const getEventsNewestFirst = (events = []) => [...events].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));

const DaySmartDashboard = () => {
    const navigate = useNavigate();
    const { currentUser, employees: storeEmployees } = useUserStore();
    
    // Browser calling state
    const [twilioInitialized, setTwilioInitialized] = useState(false);
    const [activeCallPhone, setActiveCallPhone] = useState(null);
    const [callStatus, setCallStatus] = useState(null);
    
    // Build DaySmart ID -> employee name lookup from store employees
    const daysmartIdToName = React.useMemo(() => {
        const lookup = {};
        if (storeEmployees && Array.isArray(storeEmployees)) {
            storeEmployees.forEach(emp => {
                const idMap = emp.location_to_daysmart_id_map;
                if (idMap && typeof idMap === 'object') {
                    Object.values(idMap).forEach(dsId => {
                        if (dsId) {
                            lookup[dsId] = emp.first_name;
                        }
                    });
                }
            });
        }
        return lookup;
    }, [storeEmployees]);
    
    // State
    const [activeTab, setActiveTab] = useState('appointments');
    const [selectedLocation, setSelectedLocation] = useState(DAYSMART_LOCATIONS.ALL);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Data
    const [appointments, setAppointments] = useState([]);
    const [employees, setEmployees] = useState({ hopkins: [], inverGrove: [] });
    const [clientNotes, setClientNotes] = useState([]);
    
    // Client lookup (notes by ID)
    const [clientSearchId, setClientSearchId] = useState('');
    const [clientSearchLocation, setClientSearchLocation] = useState(1);
    
    // Client search
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [clientSearchType, setClientSearchType] = useState(CLIENT_SEARCH_TYPES.ALL);
    const [clientSearchResults, setClientSearchResults] = useState([]);
    const [clientSearchPerformed, setClientSearchPerformed] = useState(false);
    
    // Appointment details modal
    const [ticketDetails, setTicketDetails] = useState(null);
    const [ticketLoading, setTicketLoading] = useState(false);
    const [ticketError, setTicketError] = useState(null);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [modalViewOnly, setModalViewOnly] = useState(false);
    
    // Groomer availability modal
    const [showGroomerModal, setShowGroomerModal] = useState(false);
    const [groomerModalEmployee, setGroomerModalEmployee] = useState(null);
    const [groomerModalLocationId, setGroomerModalLocationId] = useState(null);
    
    // Scheduling tab
    const [schedulingDate, setSchedulingDate] = useState(new Date().toISOString().split('T')[0]);
    const [schedulingData, setSchedulingData] = useState([]); // Array of { client, appointments }
    const [schedulingLoading, setSchedulingLoading] = useState(false);
    
    // Reminder Calls tab
    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };
    const [reminderDate, setReminderDate] = useState(getTomorrowDate());
    const [reminderData, setReminderData] = useState([]); // Array of appointments for reminder calls
    const [reminderLoading, setReminderLoading] = useState(false);
    const [selectedAppointments, setSelectedAppointments] = useState(new Set()); // Set of apptKey strings
    const [reminderChains, setReminderChains] = useState({}); // { appointmentId: chain }
    const [groombotCalling, setGroombotCalling] = useState(false);
    const [groombotProgress, setGroombotProgress] = useState({ completed: 0, total: 0, current: null });
    
    // Filters
    const [viewMode, setViewMode] = useState('calendar'); // 'calendar', 'list', 'employee'
    const [statusFilter] = useState('all');
    const [showClosedAppointments, setShowClosedAppointments] = useState(false);
    
    // Location phone numbers for email signatures
    const [locationPhones, setLocationPhones] = useState({});
    
    // Appointment pattern suggestions per client
    const [clientSuggestions, setClientSuggestions] = useState({}); // { clientId: { loading, data, error } }
    const [expandedSuggestions, setExpandedSuggestions] = useState({}); // { clientId: boolean }
    
    // Client lookup - appointment data per client
    const [clientLookupAppointments, setClientLookupAppointments] = useState({}); // { `${locationId}-${clientId}`: { loading, upcoming, previous, expanded } }
    
    // Client lookup - communication chains per client
    const [clientLookupChains, setClientLookupChains] = useState({}); // { `${locationId}-${clientId}`: { loading, chains, expanded, error } }
    
    // Calendar grid constants
    const HOUR_HEIGHT = 60; // pixels per hour
    const START_HOUR = 7; // 7 AM
    const END_HOUR = 20; // 8 PM
    const hours = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

    // Initiate outbound call via browser and record communication chain event
    const handleCallClient = async (phone, clientContext) => {
        const cleanPhone = phone.replace(/\D/g, '');
        
        // Initialize Twilio if needed
        if (!twilioInitialized && currentUser?.user_id) {
            try {
                await initializeTwilioDevice(`agent_${currentUser.user_id}`);
                setTwilioInitialized(true);
            } catch (err) {
                alert('Failed to initialize phone system: ' + err.message);
                return;
            }
        }
        
        if (!window.confirm(`Call ${formatPhoneNumber(cleanPhone)}?`)) return;
        
        try {
            setActiveCallPhone(cleanPhone);
            setCallStatus('ringing');
            await makeCall(cleanPhone, (status) => {
                setCallStatus(status);
                if (status === 'disconnected' || status === 'cancelled' || status === 'rejected') {
                    setActiveCallPhone(null);
                    setTimeout(() => setCallStatus(null), 2000);
                }
            });
            // Record communication chain event (find existing open chain or create new)
            if (clientContext?.clientName && clientContext?.locationId) {
                try {
                    await recordCommunicationEvent({
                        clientId: clientContext.clientId,
                        clientPhone: cleanPhone,
                        clientName: clientContext.clientName,
                        locationId: clientContext.locationId,
                        eventType: COMMUNICATION_EVENT_TYPES.OUTBOUND_CALL,
                        eventNotes: clientContext.eventNotes || 'Manual outbound call',
                        userId: currentUser?.user_id,
                        appointmentId: clientContext.appointmentId,
                        appointmentDate: clientContext.appointmentDate,
                    });
                } catch (chainErr) {
                    console.error('Failed to record communication chain event:', chainErr);
                }
            }
        } catch (err) {
            alert('Failed to initiate call: ' + err.message);
            setActiveCallPhone(null);
            setCallStatus(null);
        }
    };

    // Fetch appointments for selected date and location
    const loadAppointments = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const startDate = new Date(selectedDate);
            startDate.setHours(0, 0, 0, 0);
            
            const endDate = new Date(selectedDate);
            endDate.setHours(23, 59, 59, 999);
            
            const result = await fetchAppointments(
                startDate.toISOString(),
                endDate.toISOString(),
                selectedLocation,
                null,
                showClosedAppointments
            );
            
            setAppointments(result.appointments || []);
        } catch (err) {
            setError(err.message);
            setAppointments([]);
        } finally {
            setLoading(false);
        }
    }, [selectedDate, selectedLocation, showClosedAppointments]);

    // Fetch employees for both locations
    const loadEmployees = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const [hopkinsResult, inverGroveResult] = await Promise.all([
                fetchDaysmartEmployees(DAYSMART_LOCATIONS.HOPKINS),
                fetchDaysmartEmployees(DAYSMART_LOCATIONS.INVER_GROVE)
            ]);
            
            setEmployees({
                hopkins: hopkinsResult.employees || [],
                inverGrove: inverGroveResult.employees || []
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch client notes
    const loadClientNotes = async () => {
        if (!clientSearchId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const result = await fetchClientNotes(clientSearchLocation, parseInt(clientSearchId));
            setClientNotes(result.notes || []);
        } catch (err) {
            setError(err.message);
            setClientNotes([]);
        } finally {
            setLoading(false);
        }
    };

    // Search clients by name, pet, species, or breed
    const handleClientSearch = async () => {
        if (!clientSearchTerm.trim()) return;
        
        setLoading(true);
        setError(null);
        setClientSearchPerformed(true);
        
        try {
            const result = await searchClients(
                clientSearchTerm,
                clientSearchType,
                selectedLocation, // null = all locations
                100,
                false
            );
            setClientSearchResults(result.clients || []);
        } catch (err) {
            setError(err.message);
            setClientSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    // Load scheduling data - get all clients with appointments on selected date and their upcoming appointments
    const loadSchedulingData = useCallback(async () => {
        setSchedulingLoading(true);
        setError(null);
        
        try {
            // Fetch appointments for both locations on the scheduling date (include closed tickets)
            const [hopkinsAppts, inverGroveAppts] = await Promise.all([
                fetchAppointments(schedulingDate, schedulingDate, DAYSMART_LOCATIONS.HOPKINS, null, true),
                fetchAppointments(schedulingDate, schedulingDate, DAYSMART_LOCATIONS.INVER_GROVE, null, true)
            ]);
            
            // Combine and get unique clients
            const allAppts = [
                ...(hopkinsAppts.appointments || []).map(a => ({ ...a, location_id: 1 })),
                ...(inverGroveAppts.appointments || []).map(a => ({ ...a, location_id: 2 }))
            ];
            
            // Group by client_id and location to get unique clients
            const clientMap = new Map();
            allAppts.forEach(appt => {
                if (appt.client_id) {
                    const key = `${appt.location_id}-${appt.client_id}`;
                    if (!clientMap.has(key)) {
                        clientMap.set(key, {
                            client_id: appt.client_id,
                            client_name: appt.client_name || 'Unknown',
                            client_email: appt.client_email || null,
                            location_id: appt.location_id,
                            todayAppointments: []
                        });
                    } else if (appt.client_email && !clientMap.get(key).client_email) {
                        // Update email if we find it in a later appointment
                        clientMap.get(key).client_email = appt.client_email;
                    }
                    clientMap.get(key).todayAppointments.push(appt);
                }
            });
            
            // Fetch upcoming and previous appointments for each unique client
            const clientsWithAppointments = await Promise.all(
                Array.from(clientMap.values()).map(async (client) => {
                    try {
                        const [upcomingResult, previousResult] = await Promise.all([
                            getUpcomingAppointments(client.client_id, client.location_id),
                            getPreviousAppointments(client.client_id, client.location_id)
                        ]);
                        return {
                            ...client,
                            upcomingAppointments: upcomingResult.appointments || [],
                            previousAppointments: previousResult.appointments || []
                        };
                    } catch (err) {
                        console.error(`Failed to fetch appointments for client ${client.client_id}:`, err);
                        return {
                            ...client,
                            upcomingAppointments: [],
                            previousAppointments: []
                        };
                    }
                })
            );
            
            // Helper to count consolidated appointments (group by date)
            const countConsolidated = (services) => {
                if (!services || services.length === 0) return 0;
                const dates = new Set(services.map(s => new Date(s.start_time).toDateString()));
                return dates.size;
            };
            
            // Sort by number of consolidated appointments (ascending) so clients with fewer show first
            clientsWithAppointments.sort((a, b) => countConsolidated(a.upcomingAppointments) - countConsolidated(b.upcomingAppointments));
            
            setSchedulingData(clientsWithAppointments);
        } catch (err) {
            setError(err.message);
            setSchedulingData([]);
        } finally {
            setSchedulingLoading(false);
        }
    }, [schedulingDate]);

    // Load reminder calls data - appointments for the selected date
    const loadReminderData = useCallback(async () => {
        setReminderLoading(true);
        setError(null);
        setSelectedAppointments(new Set()); // Clear selections on refresh
        
        try {
            // Fetch appointments and reminder chains in parallel
            const [hopkinsAppts, inverGroveAppts, chainsData] = await Promise.all([
                fetchAppointments(reminderDate, reminderDate, DAYSMART_LOCATIONS.HOPKINS, null, false),
                fetchAppointments(reminderDate, reminderDate, DAYSMART_LOCATIONS.INVER_GROVE, null, false),
                fetchReminderChains(reminderDate).catch(() => ({ chains: [] }))
            ]);
            
            // Combine appointments from both locations
            const allAppts = [
                ...(hopkinsAppts.appointments || []).map(a => ({ ...a, location_id: 1 })),
                ...(inverGroveAppts.appointments || []).map(a => ({ ...a, location_id: 2 }))
            ];
            
            // Sort by time
            allAppts.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
            
            setReminderData(allAppts);
            
            // Build chains map by appointment_id
            const chainsMap = {};
            if (chainsData?.chains) {
                chainsData.chains.forEach(chain => {
                    if (chain.appointment_id) {
                        chainsMap[`${chain.location_id}-${chain.appointment_id}`] = chain;
                    }
                });
            }
            setReminderChains(chainsMap);
        } catch (err) {
            setError(err.message);
            setReminderData([]);
        } finally {
            setReminderLoading(false);
        }
    }, [reminderDate]);

    // Toggle selection for a single appointment
    const toggleAppointmentSelection = (apptKey) => {
        setSelectedAppointments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(apptKey)) {
                newSet.delete(apptKey);
            } else {
                newSet.add(apptKey);
            }
            return newSet;
        });
    };

    // Select/deselect all appointments
    const toggleSelectAll = () => {
        if (selectedAppointments.size > 0) {
            // Any selected → deselect all
            setSelectedAppointments(new Set());
        } else {
            // Select only "not reminded" appointments that have a phone number
            const notRemindedKeys = new Set(
                reminderData
                    .filter(a => {
                        if (!a.client_cell_phone) return false;
                        const chain = reminderChains[`${a.location_id}-${a.appointment_id}`];
                        const hasChainEvents = chain && chain.events && chain.events.length > 0;
                        const hasDaySmartConfirmation = !!a.confirmation_type;
                        return !hasChainEvents && !hasDaySmartConfirmation;
                    })
                    .map(a => `${a.location_id}-${a.appointment_id}`)
            );
            setSelectedAppointments(notRemindedKeys);
        }
    };

    // Handle GroomBot reminder calls for selected appointments
    const handleGroombotReminderCalls = async (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        
        console.log('handleGroombotReminderCalls called');
        
        if (selectedAppointments.size === 0) {
            alert('Please select at least one appointment to call.');
            return;
        }

        const selectedAppts = reminderData.filter(a => 
            selectedAppointments.has(`${a.location_id}-${a.appointment_id}`) && a.client_cell_phone
        );

        if (selectedAppts.length === 0) {
            alert('No valid appointments selected (must have phone number).');
            return;
        }

        if (!window.confirm(`Have GroomBot call ${selectedAppts.length} client(s) to remind them of their appointment?`)) {
            return;
        }

        setGroombotCalling(true);
        setGroombotProgress({ completed: 0, total: selectedAppts.length, current: null });

        try {
            // Format appointments for the API
            const formattedAppts = selectedAppts.map(a => ({
                client_phone: a.client_cell_phone,
                appointment_id: a.appointment_id,
                appointment_date: reminderDate,
                appointment_time: formatTime(a.start_time),
                location_id: a.location_id,
                client_name: a.client_name || 'Client',
                client_id: a.client_id,
                pet_name: a.pet_name || '',
            }));

            await triggerBatchGroombotReminderCalls(formattedAppts, (progress) => {
                setGroombotProgress(progress);
            });

            // Refresh data to get updated chain statuses
            await loadReminderData();
            setSelectedAppointments(new Set());
        } catch (err) {
            setError('Failed to complete GroomBot calls: ' + err.message);
        } finally {
            setGroombotCalling(false);
        }
    };

    // Handle clicking on an appointment to view details
    const handleAppointmentClick = async (appointment, viewOnly = false) => {
        setShowAppointmentModal(true);
        setModalViewOnly(viewOnly);
        setTicketLoading(true);
        setTicketError(null);
        setTicketDetails(null);
        
        try {
            const result = await fetchTicketDetails(appointment.appointment_id, appointment.location_id);
            setTicketDetails(result.ticket);
        } catch (err) {
            setTicketError(err.message);
        } finally {
            setTicketLoading(false);
        }
    };

    // Close appointment details modal
    const handleCloseAppointmentModal = useCallback(() => {
        setShowAppointmentModal(false);
        setTicketDetails(null);
        setTicketError(null);
    }, []);

    // Handle payment completion from Square terminal
    const handlePaymentComplete = useCallback(async (paymentData) => {
        console.log('Payment completed:', paymentData);
        // paymentData contains: ticketId, locationId, checkoutId, amountCents, tipCents, totalCents, paymentIds
        
        try {
            // Convert cents to dollars for DaySmart
            const paymentAmountDollars = paymentData.totalCents / 100;
            const tipAmountDollars = (paymentData.tipCents || 0) / 100;
            
            // Calculate proportional tip split across employees
            // Sum services by employee, then allocate tip proportionally
            let tips = [];
            if (tipAmountDollars > 0 && ticketDetails?.items?.length > 0) {
                // Group service totals by employee
                const employeeServiceTotals = {};
                let totalServices = 0;
                
                for (const item of ticketDetails.items) {
                    if (item.employee_id && item.row_total) {
                        const empId = item.employee_id;
                        const itemTotal = parseFloat(item.row_total) || 0;
                        employeeServiceTotals[empId] = (employeeServiceTotals[empId] || 0) + itemTotal;
                        totalServices += itemTotal;
                    }
                }
                
                // Calculate tip share for each employee based on proportion
                if (totalServices > 0) {
                    for (const [empId, serviceTotal] of Object.entries(employeeServiceTotals)) {
                        const proportion = serviceTotal / totalServices;
                        const tipShare = Math.round(tipAmountDollars * proportion * 100) / 100; // Round to 2 decimals
                        if (tipShare > 0) {
                            tips.push({
                                employee_id: parseInt(empId, 10),
                                amount: tipShare,
                            });
                        }
                    }
                }
                
            }
            
            console.log('Closing ticket in DaySmart:', {
                ticketId: paymentData.ticketId,
                locationId: paymentData.locationId,
                paymentAmount: paymentAmountDollars,
                tips,
            });
            
            const result = await closeTicket(
                paymentData.ticketId,
                paymentData.locationId,
                paymentAmountDollars,
                tips
            );
            
            console.log('Ticket closed successfully:', result);
            
            // Refresh appointments to show updated status
            loadAppointments();
            
            // Don't close the modal - let it stay open to show upcoming appointments
            // The modal will auto-close after 60 seconds of inactivity
            
        } catch (error) {
            console.error('Failed to close ticket in DaySmart:', error);
            // Don't close the modal on error - let user see the payment success and try again manually if needed
            alert(`Payment successful, but failed to close ticket in DaySmart: ${error.message}`);
        }
    }, [ticketDetails, loadAppointments]);

    // Load data when tab changes
    useEffect(() => {
        if (activeTab === 'appointments') {
            loadAppointments();
        } else if (activeTab === 'employees') {
            loadEmployees();
        } else if (activeTab === 'scheduling') {
            loadSchedulingData();
        } else if (activeTab === 'reminders') {
            loadReminderData();
        }
    }, [activeTab, loadAppointments, loadEmployees, loadSchedulingData, loadReminderData]);

    // Reload appointments when date/location changes
    useEffect(() => {
        if (activeTab === 'appointments') {
            loadAppointments();
        }
    }, [selectedDate, selectedLocation, activeTab, loadAppointments]);

    // Reload scheduling data when scheduling date changes
    useEffect(() => {
        if (activeTab === 'scheduling') {
            loadSchedulingData();
        }
    }, [schedulingDate, activeTab, loadSchedulingData]);

    // Reload reminder data when reminder date changes
    useEffect(() => {
        if (activeTab === 'reminders') {
            loadReminderData();
        }
    }, [reminderDate, activeTab, loadReminderData]);

    // Load location phone numbers on mount
    useEffect(() => {
        const loadLocationPhones = async () => {
            try {
                const [hopkinsPhone, inverGrovePhone] = await Promise.all([
                    getLocationPhone(DAYSMART_LOCATIONS.HOPKINS),
                    getLocationPhone(DAYSMART_LOCATIONS.INVER_GROVE)
                ]);
                setLocationPhones({
                    [DAYSMART_LOCATIONS.HOPKINS]: hopkinsPhone,
                    [DAYSMART_LOCATIONS.INVER_GROVE]: inverGrovePhone
                });
            } catch (err) {
                console.error('Failed to load location phones:', err);
            }
        };
        loadLocationPhones();
    }, []);

    // Format time from ISO string
    const formatTime = (isoString) => {
        if (!isoString) return '-';
        const date = new Date(isoString);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    // Open Gmail compose with appointment reminder email
    const handleSendAppointmentEmail = async (client, upcomingAppointments, todayAppointments, preConsolidated = null) => {
        // Look up email on-the-fly using the most recent appointment's ticket details
        let clientEmail = client.client_email;
        
        if (!clientEmail) {
            // Try to get email from today's appointment or the most recent one
            const appointmentToLookup = todayAppointments?.[0] || upcomingAppointments?.[0];
            
            if (appointmentToLookup?.appointment_id) {
                try {
                    const ticketDetails = await fetchTicketDetails(appointmentToLookup.appointment_id, client.location_id);
                    clientEmail = ticketDetails?.ticket?.client_email;
                } catch (err) {
                    console.error('Failed to fetch ticket details for email:', err);
                }
            }
        }
        
        if (!clientEmail) {
            alert('No email address on file for this client.');
            return;
        }

        // Format the appointments list
        const consolidated = preConsolidated || consolidateAppointments(upcomingAppointments, client.location_id);
        const appointmentLines = consolidated.map(appt => {
            const date = new Date(appt.start_time);
            const dateStr = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
            const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
            return `- ${dateStr} at ${timeStr}: ${appt.pet_names.join(' & ')}`;
        }).join('\n');

        const subject = 'Zoom Groom Appointments';
        const rawPhone = locationPhones[client.location_id] || '';
        const formattedPhone = rawPhone.length === 10 
            ? `(${rawPhone.slice(0, 3)}) ${rawPhone.slice(3, 6)}-${rawPhone.slice(6)}`
            : rawPhone;
        const bookingUrl = client.location_id === 1 
            ? 'https://www.zoomgroomllc.com/book/hopkins'
            : 'https://www.zoomgroomllc.com/book/invergrove';
        
        const signature = `
Thank you,
Zoom Groom Reception Team
${formattedPhone}

Now offering online booking!
${bookingUrl}`;
        
        const body = consolidated.length > 0 
            ? `Hello,

Here are your upcoming appointments at Zoom Groom:
${appointmentLines}
${signature}`
            : `Hello,

We noticed you don't have any upcoming appointments scheduled at Zoom Groom. We'd love to see you and your pet(s) again soon!

Please give us a call or book online to schedule your next visit.
${signature}`;

        const to = encodeURIComponent(clientEmail);
        const su = encodeURIComponent(subject);
        const bodyEncoded = encodeURIComponent(body);
        const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${su}&body=${bodyEncoded}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    // Print client's upcoming appointments
    const handlePrintClientAppointments = (client, upcomingAppointments, preConsolidated = null) => {
        const consolidated = preConsolidated || consolidateAppointments(upcomingAppointments, client.location_id);
        const locationName = client.location_id === 1 ? 'Hopkins' : 'Inver Grove Heights';
        
        const appointmentRows = consolidated.map(appt => {
            const date = new Date(appt.start_time);
            const dateStr = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
            const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
            return `<tr>
                <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${dateStr}</td>
                <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${timeStr}</td>
                <td style="padding: 8px; border-bottom: 1px solid #e5e7eb;">${appt.pet_names.join(' & ')}</td>
            </tr>`;
        }).join('');

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Appointments - ${client.client_name}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h1 { color: #1f2937; margin-bottom: 5px; }
                    h2 { color: #6b7280; font-weight: normal; margin-top: 0; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                    th { text-align: left; padding: 8px; background-color: #f3f4f6; border-bottom: 2px solid #d1d5db; }
                    .header { border-bottom: 2px solid #7c3aed; padding-bottom: 10px; margin-bottom: 20px; }
                    .no-appointments { color: #6b7280; font-style: italic; margin-top: 20px; }
                    @media print { body { padding: 0; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>Zoom Groom - ${locationName}</h1>
                    <h2>Upcoming Appointments for ${client.client_name}</h2>
                </div>
                ${consolidated.length > 0 ? `
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Pet</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${appointmentRows}
                        </tbody>
                    </table>
                ` : '<p class="no-appointments">No upcoming appointments scheduled.</p>'}
                <script>window.onload = function() { window.print(); window.close(); }</script>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
    };

    // Load appointment data for a client in the lookup tab
    const loadClientLookupAppointments = async (clientId, locationId) => {
        const key = `${locationId}-${clientId}`;
        
        // Toggle expansion
        setClientLookupAppointments(prev => ({
            ...prev,
            [key]: { ...prev[key], expanded: !prev[key]?.expanded }
        }));
        
        // If already loaded or loading, just toggle
        if (clientLookupAppointments[key]?.upcoming || clientLookupAppointments[key]?.loading) {
            return;
        }
        
        setClientLookupAppointments(prev => ({
            ...prev,
            [key]: { ...prev[key], loading: true, expanded: true }
        }));
        
        try {
            const [upcomingRes, previousRes] = await Promise.all([
                getUpcomingAppointments(clientId, locationId),
                getPreviousAppointments(clientId, locationId)
            ]);
            // API returns { appointments: [...] }, extract the array
            const upcoming = upcomingRes?.appointments || [];
            const previous = previousRes?.appointments || [];
            setClientLookupAppointments(prev => ({
                ...prev,
                [key]: { loading: false, upcoming, previous, expanded: true }
            }));
        } catch (err) {
            console.error('Failed to load client appointments:', err);
            setClientLookupAppointments(prev => ({
                ...prev,
                [key]: { loading: false, upcoming: [], previous: [], expanded: true, error: err.message }
            }));
        }
    };

    // Load communication chains for a client in the lookup tab
    const loadClientLookupChainsData = async (clientId, locationId, clientPhone) => {
        const key = `${locationId}-${clientId}`;
        
        // Toggle expansion
        setClientLookupChains(prev => ({
            ...prev,
            [key]: { ...prev[key], expanded: !prev[key]?.expanded }
        }));
        
        // If already loaded or loading, just toggle
        if (clientLookupChains[key]?.chains || clientLookupChains[key]?.loading) {
            return;
        }
        
        setClientLookupChains(prev => ({
            ...prev,
            [key]: { ...prev[key], loading: true, expanded: true }
        }));
        
        try {
            const result = await fetchClientCommunicationChains(clientId, locationId, clientPhone);
            setClientLookupChains(prev => ({
                ...prev,
                [key]: { loading: false, chains: result.chains || [], expanded: true, error: null }
            }));
        } catch (err) {
            console.error('Failed to load client communication chains:', err);
            setClientLookupChains(prev => ({
                ...prev,
                [key]: { loading: false, chains: [], expanded: true, error: err.message }
            }));
        }
    };

    // Load appointment pattern suggestions for a client
    const loadSuggestionsForClient = async (clientId, locationId) => {
        // Toggle expansion
        setExpandedSuggestions(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
        
        // If already loaded or loading, just toggle
        if (clientSuggestions[clientId]?.data || clientSuggestions[clientId]?.loading) {
            return;
        }
        
        setClientSuggestions(prev => ({
            ...prev,
            [clientId]: { loading: true, data: null, error: null }
        }));
        
        try {
            const patterns = await getAppointmentPatterns(clientId, locationId);
            setClientSuggestions(prev => ({
                ...prev,
                [clientId]: { loading: false, data: patterns, error: null }
            }));
        } catch (err) {
            console.error('Failed to load suggestions:', err);
            setClientSuggestions(prev => ({
                ...prev,
                [clientId]: { loading: false, data: null, error: err.message }
            }));
        }
    };

    // Consolidate services into appointments by date/time (for upcoming - lowercase fields)
    const consolidateAppointments = (services, locationId) => {
        if (!services || services.length === 0) return [];
        
        const grouped = new Map();
        services.forEach(svc => {
            // Group by date (just the date part, not time)
            const dateKey = new Date(svc.start_time).toDateString();
            if (!grouped.has(dateKey)) {
                grouped.set(dateKey, {
                    date: dateKey,
                    start_time: svc.start_time,
                    pet_names: new Set(),
                    employee_name: svc.employee_name,
                    ticket_id: svc.ticket_id,
                    location_id: locationId,
                    services: []
                });
            }
            if (svc.pet_name) {
                grouped.get(dateKey).pet_names.add(svc.pet_name);
            }
            grouped.get(dateKey).services.push(svc.item_description);
        });
        
        // Convert to array, keep pet_names as array, and sort by date
        return Array.from(grouped.values()).map(appt => ({
            ...appt,
            pet_names: Array.from(appt.pet_names)
        })).sort((a, b) => 
            new Date(a.start_time) - new Date(b.start_time)
        );
    };

    // Consolidate previous services into appointments by date (for previous - Pascal case fields from DaySmart API)
    const consolidatePreviousAppointments = (services, locationId, limit = 5) => {
        if (!services || services.length === 0) return [];
        
        const grouped = new Map();
        services.forEach(svc => {
            // Group by date (just the date part, not time)
            const dateKey = new Date(svc.DateScheduled || svc.StartTime).toDateString();
            if (!grouped.has(dateKey)) {
                grouped.set(dateKey, {
                    date: dateKey,
                    start_time: svc.StartTime,
                    pet_name: svc.PetName,
                    employee_name: svc.EmployeeName,
                    ticket_id: svc.TicketID,
                    location_id: locationId,
                    services: [],
                    duration: 0
                });
            }
            grouped.get(dateKey).services.push(svc.ItemDescription);
            grouped.get(dateKey).duration += (svc.Duration || 0);
        });
        
        // Convert to array, sort by date descending (most recent first), take only the limit
        return Array.from(grouped.values())
            .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
            .slice(0, limit);
    };

    // Get status color
    const getStatusColor = (status) => {
        const s = (status || '').toLowerCase();
        if (s.includes('confirm')) return 'bg-green-100 text-green-800';
        if (s.includes('check') && s.includes('in')) return 'bg-blue-100 text-blue-800';
        if (s.includes('complete')) return 'bg-gray-100 text-gray-800';
        if (s.includes('cancel')) return 'bg-red-100 text-red-800';
        if (s.includes('no show')) return 'bg-orange-100 text-orange-800';
        return 'bg-yellow-100 text-yellow-800';
    };

    // Filter and group appointments
    const getDisplayedAppointments = () => {
        let filtered = appointments;
        
        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(a => 
                (a.status || '').toLowerCase().includes(statusFilter.toLowerCase())
            );
        }
        
        // Sort by time
        filtered = [...filtered].sort((a, b) => 
            new Date(a.start_time) - new Date(b.start_time)
        );
        
        // Group if needed
        if (viewMode === 'employee') {
            return groupAppointmentsByEmployee(filtered);
        }
        
        return filtered;
    };
    
    // Get unique employees from appointments for calendar columns
    const getCalendarEmployees = () => {
        const empMap = new Map();
        appointments.forEach(appt => {
            const empId = appt.employee_id;
            if (empId && !empMap.has(empId)) {
                const name = appt.employee_name || daysmartIdToName[empId] || `Employee ${empId}`;
                empMap.set(empId, { id: empId, name });
            }
        });
        // Sort by name
        return Array.from(empMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    };
    
    // Calculate appointment position and height for calendar view
    const getAppointmentStyle = (appointment) => {
        const startDate = new Date(appointment.start_time);
        const endDate = appointment.end_time ? new Date(appointment.end_time) : new Date(startDate.getTime() + 60 * 60 * 1000); // Default 1 hour
        
        const startHour = startDate.getHours() + startDate.getMinutes() / 60;
        const endHour = endDate.getHours() + endDate.getMinutes() / 60;
        
        const top = (startHour - START_HOUR) * HOUR_HEIGHT;
        const height = Math.max((endHour - startHour) * HOUR_HEIGHT, 30); // Min 30px height
        
        return { top, height };
    };
    
    // Get appointment color based on status
    const getAppointmentColor = (status) => {
        const s = (status || '').toLowerCase();
        if (s.includes('confirm')) return 'bg-green-200 border-green-400 text-green-900';
        if (s.includes('check') && s.includes('in')) return 'bg-blue-200 border-blue-400 text-blue-900';
        if (s.includes('complete')) return 'bg-gray-200 border-gray-400 text-gray-700';
        if (s.includes('cancel')) return 'bg-red-200 border-red-400 text-red-900';
        if (s.includes('no show')) return 'bg-orange-200 border-orange-400 text-orange-900';
        return 'bg-purple-200 border-purple-400 text-purple-900';
    };
    
    // Render calendar grid view
    const renderCalendarGrid = () => {
        const calendarEmployees = getCalendarEmployees();
        
        if (calendarEmployees.length === 0) {
            return (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                    No employees with appointments found.
                </div>
            );
        }
        
        // Group appointments by employee
        const appointmentsByEmployee = {};
        appointments.forEach(appt => {
            const empId = appt.employee_id;
            if (empId) {
                if (!appointmentsByEmployee[empId]) {
                    appointmentsByEmployee[empId] = [];
                }
                appointmentsByEmployee[empId].push(appt);
            }
        });
        
        const columnWidth = Math.max(150, Math.floor((window.innerWidth - 120) / Math.min(calendarEmployees.length, 8)));
        
        return (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                {/* Single scrollable container for header + body */}
                <div style={{ minWidth: 'fit-content' }}>
                    {/* Header with employee names */}
                    <div className="flex border-b bg-gray-50 sticky top-0 z-10">
                        <div className="w-16 flex-shrink-0 border-r bg-gray-100 p-2 text-xs font-semibold text-gray-500 text-center sticky left-0 z-20">
                            Time
                        </div>
                        {calendarEmployees.map(emp => (
                            <div
                                key={emp.id}
                                className="flex-shrink-0 border-r p-2 text-center font-medium text-sm text-gray-700 bg-gray-50"
                                style={{ width: columnWidth }}
                            >
                                {emp.name}
                            </div>
                        ))}
                    </div>
                    
                    {/* Calendar body */}
                    <div className="flex">
                        {/* Time labels column - sticky left */}
                        <div className="w-16 flex-shrink-0 border-r bg-gray-50 sticky left-0 z-10">
                            {hours.map(hour => (
                                <div
                                    key={hour}
                                    className="border-b text-xs text-gray-500 text-right pr-2 pt-1"
                                    style={{ height: HOUR_HEIGHT }}
                                >
                                    {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                                </div>
                            ))}
                        </div>
                        
                        {/* Employee columns with appointments */}
                        {calendarEmployees.map(emp => (
                            <div
                                key={emp.id}
                                className="flex-shrink-0 border-r relative"
                                style={{ width: columnWidth, height: hours.length * HOUR_HEIGHT }}
                            >
                                {/* Hour grid lines */}
                                {hours.map(hour => (
                                    <div
                                        key={hour}
                                        className="border-b border-gray-100"
                                        style={{ height: HOUR_HEIGHT }}
                                    />
                                ))}
                                
                                {/* Appointments */}
                                {(appointmentsByEmployee[emp.id] || []).map((appt, idx) => {
                                    const style = getAppointmentStyle(appt);
                                    return (
                                        <div
                                            key={idx}
                                            className={`absolute left-1 right-1 rounded border-l-4 px-1 py-0.5 overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${getAppointmentColor(appt.status)}`}
                                            style={{ top: style.top, height: style.height }}
                                            title={`${appt.client_name || 'Unknown'}\n${appt.service_name || ''}\n${formatTime(appt.start_time)} - ${formatTime(appt.end_time)}`}
                                            onClick={() => handleAppointmentClick(appt)}
                                        >
                                            <div className="text-xs font-medium truncate">
                                                {appt.client_name || 'Unknown'}
                                            </div>
                                            {style.height > 40 && (
                                                <div className="text-xs truncate opacity-75">
                                                    {appt.pet_name && `🐾 ${appt.pet_name}`}
                                                </div>
                                            )}
                                            {style.height > 55 && (
                                                <div className="text-xs truncate opacity-75">
                                                    {appt.service_name}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Render appointments based on view mode
    const renderAppointments = () => {
        if (viewMode === 'calendar') {
            return renderCalendarGrid();
        }
        
        const data = getDisplayedAppointments();
        
        if (viewMode === 'employee') {
            return (
                <div className="space-y-6">
                    {Object.entries(data).map(([empId, group]) => (
                        <div key={empId} className="bg-white rounded-lg shadow">
                            <div className="bg-purple-50 px-4 py-2 rounded-t-lg border-b">
                                <h3 className="font-semibold text-purple-800">
                                    {group.employee_name} ({group.appointments.length} appointments)
                                </h3>
                            </div>
                            <div className="divide-y">
                                {group.appointments.map((appt, idx) => (
                                    <AppointmentRow key={idx} appointment={appt} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
        
        return (
            <div className="bg-white rounded-lg shadow">
                <AppointmentHeader />
                <div className="divide-y">
                    {data.map((appt, idx) => (
                        <AppointmentRow key={idx} appointment={appt} />
                    ))}
                </div>
            </div>
        );
    };

    // Appointment table header
    const AppointmentHeader = () => (
        <div className="px-4 py-2 bg-gray-50 border-b flex items-center gap-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <div className="w-20">Time</div>
            <div className="flex-1">Client / Pet / Service</div>
            {viewMode !== 'employee' && <div className="w-24">Employee</div>}
            <div className="w-20">Status</div>
            <div className="w-20">Location</div>
        </div>
    );

    // Appointment row component
    const AppointmentRow = ({ appointment }) => {
        // Look up employee name from DaySmart ID
        const employeeName = appointment.employee_name || 
            (appointment.employee_id ? daysmartIdToName[appointment.employee_id] : null) || 
            'Unassigned';
        
        return (
            <div 
                className="px-4 py-3 hover:bg-purple-50 flex items-center gap-4 cursor-pointer transition-colors"
                onClick={() => handleAppointmentClick(appointment)}
            >
                <div className="w-20 text-sm font-medium text-gray-600">
                    {formatTime(appointment.start_time)}
                </div>
                <div className="flex-1">
                    <div className="font-medium">{appointment.client_name || 'Unknown Client'}</div>
                    <div className="text-sm text-gray-500">
                        {appointment.pet_name && <span className="mr-3">🐾 {appointment.pet_name}</span>}
                        {appointment.service_name && <span>{appointment.service_name}</span>}
                    </div>
                </div>
                {viewMode !== 'employee' && (
                    <div className="w-24 text-sm text-gray-600">
                        {employeeName}
                    </div>
                )}
                <div className={`w-20 px-2 py-1 rounded text-xs font-medium text-center ${getStatusColor(appointment.status)}`}>
                    {appointment.status || 'Unknown'}
                </div>
                <div className="w-20 text-xs text-gray-400">
                    {appointment.location_id === 1 ? 'Hopkins' : 'Inver Grove'}
                </div>
            </div>
        );
    };

    // Check auth (user 126 has special access)
    if (!currentUser || !hasPermission('view_daysmart')) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow text-center">
                    <h2 className="text-xl font-semibold text-red-600 mb-4">Access Denied</h2>
                    <p className="text-gray-600 mb-4">You need manager privileges to access this page.</p>
                    <button
                        onClick={() => navigate('/timekeeping')}
                        className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                    >
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <header className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/timekeeping')}
                            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                        >
                            <ChevronLeft size={20} />
                            Back
                        </button>
                        <h1 className="text-2xl font-bold text-purple-800">DaySmart Dashboard</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Active call indicator */}
                        {activeCallPhone && (
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                                callStatus === 'connected' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                                <PhoneCall className="w-4 h-4 animate-pulse" />
                                <span className="text-sm font-medium">
                                    {callStatus === 'ringing' ? 'Calling...' : 'Connected'} {formatPhoneNumber(activeCallPhone)}
                                </span>
                                <button
                                    onClick={() => {
                                        hangupCall();
                                        setActiveCallPhone(null);
                                        setCallStatus(null);
                                    }}
                                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    title="Hang up"
                                >
                                    <PhoneOff className="w-3 h-3" />
                                </button>
                            </div>
                        )}
                        <div className="text-sm text-gray-500">
                            Logged in as {currentUser?.user_id}
                        </div>
                    </div>
                </div>
            </header>

            {/* Tabs */}
            <div className="max-w-7xl mx-auto px-4 mt-4">
                <div className="bg-white rounded-lg shadow">
                    <div className="border-b flex overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        <button
                            onClick={() => setActiveTab('appointments')}
                            className={`px-3 sm:px-6 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                                activeTab === 'appointments'
                                    ? 'border-purple-600 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Calendar size={18} className="hidden sm:block" />
                            Appointments
                        </button>
                        <button
                            onClick={() => setActiveTab('employees')}
                            className={`px-3 sm:px-6 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                                activeTab === 'employees'
                                    ? 'border-purple-600 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Users size={18} className="hidden sm:block" />
                            Employees
                        </button>
                        <button
                            onClick={() => setActiveTab('clients')}
                            className={`px-3 sm:px-6 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                                activeTab === 'clients'
                                    ? 'border-purple-600 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Search size={18} className="hidden sm:block" />
                            Clients
                        </button>
                        <button
                            onClick={() => setActiveTab('scheduling')}
                            className={`px-3 sm:px-6 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                                activeTab === 'scheduling'
                                    ? 'border-purple-600 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <CalendarDays size={18} className="hidden sm:block" />
                            Scheduling
                        </button>
                        <button
                            onClick={() => setActiveTab('reminders')}
                            className={`px-3 sm:px-6 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                                activeTab === 'reminders'
                                    ? 'border-purple-600 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Bell size={18} className="hidden sm:block" />
                            Reminders
                        </button>
                        <button
                            onClick={() => setActiveTab('availability')}
                            className={`px-3 sm:px-6 py-3 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                                activeTab === 'availability'
                                    ? 'border-purple-600 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <CalendarDays size={18} className="hidden sm:block" />
                            Availability
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {/* Appointments Tab */}
                {activeTab === 'appointments' && (
                    <div>
                        {/* Controls */}
                        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">Date:</label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="border rounded px-3 py-1.5 text-sm"
                                />
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">Location:</label>
                                <select
                                    value={selectedLocation || ''}
                                    onChange={(e) => setSelectedLocation(e.target.value ? parseInt(e.target.value) : null)}
                                    className="border rounded px-3 py-1.5 text-sm"
                                >
                                    <option value="">All Locations</option>
                                    <option value="1">Hopkins</option>
                                    <option value="2">Inver Grove</option>
                                </select>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">View:</label>
                                <select
                                    value={viewMode}
                                    onChange={(e) => setViewMode(e.target.value)}
                                    className="border rounded px-3 py-1.5 text-sm"
                                >
                                    <option value="calendar">Calendar Grid</option>
                                    <option value="list">List by Time</option>
                                    <option value="employee">Group by Employee</option>
                                </select>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="showClosedAppointments"
                                    checked={showClosedAppointments}
                                    onChange={(e) => setShowClosedAppointments(e.target.checked)}
                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                                <label htmlFor="showClosedAppointments" className="text-sm font-medium text-gray-700">
                                    Show closed appointments
                                </label>
                            </div>
                            
                            <button
                                onClick={loadAppointments}
                                disabled={loading}
                                className="flex items-center gap-2 bg-purple-600 text-white px-4 py-1.5 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                            >
                                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                            
                            <div className="ml-auto text-sm text-gray-500">
                                {appointments.length} appointments
                            </div>
                        </div>

                        {/* Appointments List */}
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <LoadingSpinner />
                            </div>
                        ) : appointments.length === 0 ? (
                            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                                No appointments found for this date.
                            </div>
                        ) : (
                            renderAppointments()
                        )}
                    </div>
                )}

                {/* Employees Tab */}
                {activeTab === 'employees' && (
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Hopkins */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="bg-blue-50 px-4 py-3 rounded-t-lg border-b">
                                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                                    <Users size={18} />
                                    Hopkins Employees ({employees.hopkins.length})
                                </h3>
                            </div>
                            {loading ? (
                                <div className="p-8 flex justify-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {employees.hopkins.map((emp, idx) => (
                                        <div key={idx} className="px-4 py-3 flex items-center justify-between">
                                            <div>
                                                <div className="font-medium">{emp.name}</div>
                                                {emp.email && (
                                                    <div className="text-sm text-gray-500">{emp.email}</div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => {
                                                        setGroomerModalEmployee(emp);
                                                        setGroomerModalLocationId(DAYSMART_LOCATIONS.HOPKINS);
                                                        setShowGroomerModal(true);
                                                    }}
                                                    className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors bg-purple-100 text-purple-700 hover:bg-purple-200"
                                                >
                                                    <CalendarDays size={14} />
                                                    Availability
                                                </button>
                                                <div className="text-xs text-gray-400">
                                                    ID: {emp.employee_id}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {employees.hopkins.length === 0 && (
                                        <div className="p-4 text-center text-gray-500">
                                            No employees found
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Inver Grove */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="bg-green-50 px-4 py-3 rounded-t-lg border-b">
                                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                                    <Users size={18} />
                                    Inver Grove Employees ({employees.inverGrove.length})
                                </h3>
                            </div>
                            {loading ? (
                                <div className="p-8 flex justify-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {employees.inverGrove.map((emp, idx) => (
                                        <div key={idx} className="px-4 py-3 flex items-center justify-between">
                                            <div>
                                                <div className="font-medium">{emp.name}</div>
                                                {emp.email && (
                                                    <div className="text-sm text-gray-500">{emp.email}</div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => {
                                                        setGroomerModalEmployee(emp);
                                                        setGroomerModalLocationId(DAYSMART_LOCATIONS.INVER_GROVE);
                                                        setShowGroomerModal(true);
                                                    }}
                                                    className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors bg-purple-100 text-purple-700 hover:bg-purple-200"
                                                >
                                                    <CalendarDays size={14} />
                                                    Availability
                                                </button>
                                                <div className="text-xs text-gray-400">
                                                    ID: {emp.employee_id}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {employees.inverGrove.length === 0 && (
                                        <div className="p-4 text-center text-gray-500">
                                            No employees found
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Client Lookup Tab */}
                {activeTab === 'clients' && (
                    <div>
                        {/* Client Search */}
                        <div className="bg-white rounded-lg shadow p-4 mb-4">
                            <h3 className="font-medium mb-3">Search Clients</h3>
                            <div className="flex flex-wrap items-end gap-4">
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Search Term
                                    </label>
                                    <input
                                        type="text"
                                        value={clientSearchTerm}
                                        onChange={(e) => setClientSearchTerm(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleClientSearch()}
                                        placeholder="Enter name, pet, species, or breed..."
                                        className="border rounded px-3 py-2 w-full"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Search By
                                    </label>
                                    <select
                                        value={clientSearchType}
                                        onChange={(e) => setClientSearchType(e.target.value)}
                                        className="border rounded px-3 py-2"
                                    >
                                        <option value={CLIENT_SEARCH_TYPES.ALL}>All Fields</option>
                                        <option value={CLIENT_SEARCH_TYPES.CLIENT}>Client Name</option>
                                        <option value={CLIENT_SEARCH_TYPES.PET}>Pet Name</option>
                                        <option value={CLIENT_SEARCH_TYPES.SPECIES}>Species</option>
                                        <option value={CLIENT_SEARCH_TYPES.BREED}>Breed</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Location
                                    </label>
                                    <select
                                        value={selectedLocation || ''}
                                        onChange={(e) => setSelectedLocation(e.target.value ? parseInt(e.target.value) : null)}
                                        className="border rounded px-3 py-2"
                                    >
                                        <option value="">All Locations</option>
                                        <option value="1">Hopkins</option>
                                        <option value="2">Inver Grove</option>
                                    </select>
                                </div>
                                <button
                                    onClick={handleClientSearch}
                                    disabled={loading || !clientSearchTerm.trim()}
                                    className="bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    <Search size={16} />
                                    Search
                                </button>
                            </div>
                        </div>

                        {/* Search Results */}
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <LoadingSpinner />
                            </div>
                        ) : clientSearchResults.length > 0 ? (
                            <div className="space-y-4">
                                <div className="text-sm text-gray-500">
                                    Found {clientSearchResults.length} client{clientSearchResults.length !== 1 ? 's' : ''}
                                </div>
                                {clientSearchResults.map((client, idx) => {
                                    const lookupKey = `${client.location_id}-${client.client_id}`;
                                    const appointmentData = clientLookupAppointments[lookupKey];
                                    const isExpanded = appointmentData?.expanded;
                                    const chainsData = clientLookupChains[lookupKey];
                                    const isChainsExpanded = chainsData?.expanded;
                                    
                                    // Transform client data for SchedulingClientCard
                                    const transformedClient = {
                                        client_id: client.client_id,
                                        client_name: client.full_name,
                                        client_email: client.email,
                                        location_id: client.location_id,
                                        todayAppointments: [], // No specific date context in lookup
                                        upcomingAppointments: appointmentData?.upcoming || [],
                                        previousAppointments: appointmentData?.previous || []
                                    };
                                    
                                    return (
                                    <div key={idx} className="bg-white rounded-lg shadow overflow-hidden">
                                        {/* Client Header */}
                                        <div className="bg-purple-50 px-4 py-3 border-b flex items-center justify-between">
                                            <div>
                                                <h4 className="font-semibold text-purple-900">
                                                    {client.full_name}
                                                </h4>
                                                <div className="text-sm text-purple-700">
                                                    {client.cell_phone && <span className="mr-4">📱 {client.cell_phone}</span>}
                                                    {client.email && <span>✉️ {client.email}</span>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="text-right text-xs text-gray-500">
                                                    <div>ID: {client.client_id}</div>
                                                    <div>{client.location_id === 1 ? 'Hopkins' : 'Inver Grove'}</div>
                                                </div>
                                                {client.cell_phone && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCallClient(client.cell_phone, {
                                                                clientId: client.client_id,
                                                                clientName: client.full_name,
                                                                locationId: client.location_id,
                                                                eventNotes: 'Outbound call from client lookup',
                                                            });
                                                        }}
                                                        className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                                                        title="Call client"
                                                    >
                                                        <PhoneOutgoing className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Pets */}
                                        {client.pets && client.pets.length > 0 && (
                                            <div className="divide-y">
                                                {client.pets.map((pet, petIdx) => (
                                                    <div key={petIdx} className="px-4 py-3 flex items-center gap-4">
                                                        <div className="text-2xl">🐾</div>
                                                        <div className="flex-1">
                                                            <div className="font-medium">{pet.name}</div>
                                                            <div className="text-sm text-gray-600">
                                                                {pet.species_name && <span className="mr-3">{pet.species_name}</span>}
                                                                {pet.breed_name && <span className="text-gray-500">• {pet.breed_name}</span>}
                                                            </div>
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {pet.sex_name && <span className="mr-3">{pet.sex_name}</span>}
                                                            {pet.weight && <span>{pet.weight}</span>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* No pets */}
                                        {(!client.pets || client.pets.length === 0) && (
                                            <div className="px-4 py-3 text-sm text-gray-500 italic">
                                                No pets on file
                                            </div>
                                        )}
                                        
                                        {/* Appointments Toggle */}
                                        <div 
                                            className="px-4 py-2 bg-gray-100 border-t cursor-pointer hover:bg-gray-200 flex items-center justify-between"
                                            onClick={() => loadClientLookupAppointments(client.client_id, client.location_id)}
                                        >
                                            <span className="text-sm font-medium text-gray-700">
                                                📅 View Appointments
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {appointmentData?.loading ? '...' : isExpanded ? '▼' : '▶'}
                                            </span>
                                        </div>
                                        
                                        {/* Expanded Appointment Card */}
                                        {isExpanded && !appointmentData?.loading && (
                                            <SchedulingClientCard
                                                client={transformedClient}
                                                schedulingDate={new Date().toISOString().split('T')[0]}
                                                consolidatedAppointments={consolidateAppointments(appointmentData?.upcoming || [], client.location_id)}
                                                consolidatePreviousAppointments={consolidatePreviousAppointments}
                                                daysmartIdToName={daysmartIdToName}
                                                expandedSuggestions={expandedSuggestions[client.client_id]}
                                                clientSuggestions={clientSuggestions[client.client_id]}
                                                onPrint={(filtered) => handlePrintClientAppointments(transformedClient, null, filtered)}
                                                onEmail={(filtered) => handleSendAppointmentEmail(transformedClient, null, [], filtered)}
                                                onAppointmentClick={handleAppointmentClick}
                                                onLoadSuggestions={() => loadSuggestionsForClient(client.client_id, client.location_id)}
                                            />
                                        )}
                                        
                                        {/* Loading state */}
                                        {appointmentData?.loading && (
                                            <div className="px-4 py-4 text-center text-gray-500">
                                                <LoadingSpinner />
                                            </div>
                                        )}
                                        
                                        {/* Communication Chains Toggle */}
                                        <div 
                                            className="px-4 py-2 bg-blue-50 border-t cursor-pointer hover:bg-blue-100 flex items-center justify-between"
                                            onClick={() => loadClientLookupChainsData(client.client_id, client.location_id, client.cell_phone)}
                                        >
                                            <span className="text-sm font-medium text-blue-700">
                                                💬 View Communication Chains
                                            </span>
                                            <span className="text-xs text-blue-500">
                                                {chainsData?.loading ? '...' : isChainsExpanded ? '▼' : '▶'}
                                            </span>
                                        </div>
                                        
                                        {/* Expanded Communication Chains */}
                                        {isChainsExpanded && !chainsData?.loading && (
                                            <div className="border-t">
                                                {chainsData?.error && (
                                                    <div className="px-4 py-2 text-sm text-red-600 bg-red-50">
                                                        {chainsData.error}
                                                    </div>
                                                )}
                                                {chainsData?.chains?.length > 0 ? (
                                                    <div className="divide-y">
                                                        {chainsData.chains.map((chain, chainIdx) => {
                                                            return (
                                                                <div key={chainIdx} className="px-4 py-3">
                                                                    <div className="flex items-center justify-between mb-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                                                                chain.chain_type === 'reminder' 
                                                                                    ? 'bg-purple-100 text-purple-700' 
                                                                                    : 'bg-gray-100 text-gray-700'
                                                                            }`}>
                                                                                {chain.chain_type || 'general'}
                                                                            </span>
                                                                            <span className={`text-xs px-2 py-0.5 rounded ${
                                                                                chain.status === 'complete' ? 'bg-green-100 text-green-700' :
                                                                                chain.status === 'resolved' ? 'bg-green-100 text-green-700' :
                                                                                chain.status === 'pending_response' ? 'bg-yellow-100 text-yellow-700' :
                                                                                'bg-blue-100 text-blue-700'
                                                                            }`}>
                                                                                {chain.status}
                                                                            </span>
                                                                            {chain.appointment_date && (
                                                                                <span className="text-xs text-gray-500">
                                                                                    Appt: {chain.appointment_date}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-xs text-gray-400">
                                                                            {chain.updated_at ? new Date(chain.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                                                                        </span>
                                                                    </div>
                                                                    {/* Events timeline */}
                                                                    {chain.events && chain.events.length > 0 && (
                                                                        <div className="ml-2 mt-1 space-y-1">
                                                                            {getEventsNewestFirst(chain.events).map((event, eventIdx) => {
                                                                                const evInfo = getEventTypeInfo(event.type);
                                                                                return (
                                                                                    <div key={eventIdx} className="flex items-start gap-2 text-xs">
                                                                                        <span className="text-gray-400 w-16 flex-shrink-0">
                                                                                            {event.timestamp ? new Date(event.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : ''}
                                                                                        </span>
                                                                                        <span className={`px-1.5 py-0.5 rounded ${evInfo?.bg || 'bg-gray-100'} ${evInfo?.text || 'text-gray-600'}`}>
                                                                                            {evInfo?.icon || '📋'} {evInfo?.label || event.type}
                                                                                        </span>
                                                                                        {event.notes && (
                                                                                            <span className="text-gray-500 truncate" title={event.notes}>
                                                                                                {event.notes}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className="px-4 py-3 text-sm text-gray-500 italic">
                                                        No communication chains found for this client
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Chains loading state */}
                                        {chainsData?.loading && (
                                            <div className="px-4 py-4 text-center text-gray-500">
                                                <LoadingSpinner />
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        ) : clientSearchPerformed ? (
                            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                                No clients found matching "{clientSearchTerm}"
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                                Enter a search term to find clients by name, pet, species, or breed.
                            </div>
                        )}

                        {/* Client Notes Lookup (secondary) */}
                        <div className="bg-white rounded-lg shadow p-4 mt-6">
                            <h3 className="font-medium mb-3 text-gray-600">Look up Client Notes by ID</h3>
                            <div className="flex flex-wrap items-end gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Location
                                    </label>
                                    <select
                                        value={clientSearchLocation}
                                        onChange={(e) => setClientSearchLocation(parseInt(e.target.value))}
                                        className="border rounded px-3 py-2 text-sm"
                                    >
                                        <option value="1">Hopkins</option>
                                        <option value="2">Inver Grove</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Client ID
                                    </label>
                                    <input
                                        type="number"
                                        value={clientSearchId}
                                        onChange={(e) => setClientSearchId(e.target.value)}
                                        placeholder="Enter client ID"
                                        className="border rounded px-3 py-2 text-sm"
                                    />
                                </div>
                                <button
                                    onClick={loadClientNotes}
                                    disabled={loading || !clientSearchId}
                                    className="bg-gray-600 text-white px-4 py-2 rounded text-sm hover:bg-gray-700 disabled:opacity-50"
                                >
                                    Get Notes
                                </button>
                            </div>
                        </div>

                        {/* Client Notes Results */}
                        {clientNotes.length > 0 && (
                            <div className="bg-white rounded-lg shadow divide-y mt-4">
                                <div className="px-4 py-2 bg-gray-50 text-sm font-medium text-gray-600">
                                    Notes for Client #{clientSearchId}
                                </div>
                                {clientNotes.map((note, idx) => (
                                    <div key={idx} className="p-4">
                                        <div className="text-sm text-gray-500 mb-1">
                                            {note.created_date && new Date(note.created_date).toLocaleDateString()}
                                            {note.created_by && ` by ${note.created_by}`}
                                        </div>
                                        <div className="text-gray-800">{note.note_text}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Scheduling Tab */}
                {activeTab === 'scheduling' && (
                    <div>
                        {/* Date Picker */}
                        <div className="bg-white rounded-lg shadow p-4 mb-4 flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">Date:</label>
                                <input
                                    type="date"
                                    value={schedulingDate}
                                    onChange={(e) => setSchedulingDate(e.target.value)}
                                    className="border rounded px-3 py-1.5 text-sm"
                                />
                            </div>
                            <button
                                onClick={loadSchedulingData}
                                disabled={schedulingLoading}
                                className="flex items-center gap-2 bg-purple-600 text-white px-4 py-1.5 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                            >
                                <RefreshCw size={16} className={schedulingLoading ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                            <div className="text-sm text-gray-500">
                                {schedulingData.length} clients with appointments
                            </div>
                        </div>

                        {/* Loading State */}
                        {schedulingLoading && (
                            <div className="flex justify-center py-12">
                                <LoadingSpinner />
                            </div>
                        )}

                        {/* Clients List */}
                        {!schedulingLoading && schedulingData.length > 0 && (
                            <div className="space-y-4">
                                {schedulingData.map((client) => {
                                    const consolidated = consolidateAppointments(client.upcomingAppointments, client.location_id);
                                    return (
                                        <SchedulingClientCard
                                            key={`${client.location_id}-${client.client_id}`}
                                            client={client}
                                            schedulingDate={schedulingDate}
                                            consolidatedAppointments={consolidated}
                                            consolidatePreviousAppointments={consolidatePreviousAppointments}
                                            daysmartIdToName={daysmartIdToName}
                                            expandedSuggestions={expandedSuggestions[client.client_id]}
                                            clientSuggestions={clientSuggestions[client.client_id]}
                                            onPrint={(filtered) => handlePrintClientAppointments(client, null, filtered)}
                                            onEmail={(filtered) => handleSendAppointmentEmail(client, null, client.todayAppointments, filtered)}
                                            onAppointmentClick={handleAppointmentClick}
                                            onLoadSuggestions={() => loadSuggestionsForClient(client.client_id, client.location_id)}
                                        />
                                    );
                                })}
                            </div>
                        )}

                        {/* Empty State */}
                        {!schedulingLoading && schedulingData.length === 0 && (
                            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                                No appointments found for the selected date.
                            </div>
                        )}
                    </div>
                )}

                {/* Reminder Calls Tab */}
                {activeTab === 'reminders' && (
                    <div>
                        {/* Date Picker and Actions */}
                        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">Reminder Date:</label>
                                <input
                                    type="date"
                                    value={reminderDate}
                                    onChange={(e) => setReminderDate(e.target.value)}
                                    className="border rounded px-3 py-1.5 text-sm"
                                />
                            </div>
                            <button
                                onClick={loadReminderData}
                                disabled={reminderLoading}
                                className="flex items-center gap-2 bg-purple-600 text-white px-4 py-1.5 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                            >
                                <RefreshCw size={16} className={reminderLoading ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                            <div className="text-sm text-gray-500">
                                {reminderData.length} appointments to remind
                            </div>
                            <button
                                onClick={async () => {
                                    const tomorrow = new Date();
                                    tomorrow.setDate(tomorrow.getDate() + 1);
                                    const dateStr = tomorrow.toISOString().split('T')[0];
                                    try {
                                        await triggerGroombotReminderCall({
                                            client_phone: '+16128865488',
                                            appointment_id: 'test-0000',
                                            appointment_date: dateStr,
                                            appointment_time: '8:00 AM',
                                            location_id: 1,
                                            client_name: 'Josh Stowell',
                                            client_id: 'test-josh',
                                            pet_name: 'Bodhi',
                                        });
                                        alert('Test reminder call triggered!');
                                    } catch (err) {
                                        alert('Test call failed: ' + err.message);
                                    }
                                }}
                                title="Test: Call Josh Stowell about Bodhi tomorrow 8am"
                                className="ml-auto p-2 rounded-full text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                            >
                                <Settings size={18} />
                            </button>
                        </div>

                        {/* Selection Controls and GroomBot Actions */}
                        <div className="bg-white rounded-lg shadow p-4 mb-4">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                {/* Status Legend */}
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">Status Legend:</h3>
                                    <div className="flex flex-wrap gap-2 text-xs">
                                        <span className="px-2 py-1 rounded bg-gray-100 text-gray-600">⏳ Not reminded</span>
                                        <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-700">🎤 Voicemail</span>
                                        <span className="px-2 py-1 rounded bg-blue-100 text-blue-700">🗣️ Spoke to</span>
                                        <span className="px-2 py-1 rounded bg-green-100 text-green-700">✅ Confirmed</span>
                                        <span className="px-2 py-1 rounded bg-red-100 text-red-700">❌ Cancelled</span>
                                        <span className="px-2 py-1 rounded bg-orange-100 text-orange-700">🔄 Rescheduled</span>
                                        <span className="px-2 py-1 rounded bg-teal-100 text-teal-700">📋 DaySmart Confirmed</span>
                                    </div>
                                </div>

                                {/* Selection and GroomBot Controls */}
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={toggleSelectAll}
                                        disabled={reminderLoading || groombotCalling}
                                        className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {selectedAppointments.size === reminderData.filter(a => a.client_cell_phone).length && reminderData.length > 0
                                            ? '☑️ Deselect All'
                                            : '☐ Select All'}
                                    </button>
                                    
                                    <button
                                        type="button"
                                        onClick={handleGroombotReminderCalls}
                                        disabled={selectedAppointments.size === 0 || groombotCalling || reminderLoading}
                                        className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-1.5 rounded text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <PhoneCall size={16} />
                                        {groombotCalling 
                                            ? `Calling... (${groombotProgress.completed}/${groombotProgress.total})`
                                            : `Call Selected (${selectedAppointments.size})`}
                                    </button>
                                </div>
                            </div>

                            {/* GroomBot Progress */}
                            {groombotCalling && (
                                <div className="mt-3 pt-3 border-t">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${(groombotProgress.completed / groombotProgress.total) * 100}%` }}
                                            />
                                        </div>
                                        <span className="text-sm text-gray-600">
                                            {groombotProgress.current?.client_name && `Calling ${groombotProgress.current.client_name}...`}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Loading State */}
                        {reminderLoading && (
                            <div className="flex justify-center py-12">
                                <LoadingSpinner />
                            </div>
                        )}

                        {/* Appointments List */}
                        {!reminderLoading && reminderData.length > 0 && (
                            <div className="space-y-3">
                                {reminderData.map((appt, idx) => {
                                    const apptKey = `${appt.location_id}-${appt.appointment_id}`;
                                    const chain = reminderChains[apptKey];
                                    const isSelected = selectedAppointments.has(apptKey);
                                    const hasPhone = !!appt.client_cell_phone;
                                    
                                    // Determine status from chain events, DaySmart confirmation, or fallback
                                    const getChainStatus = () => {
                                        if (!chain || !chain.events || chain.events.length === 0) {
                                            // Check DaySmart confirmation_type as fallback
                                            if (appt.confirmation_type) {
                                                const dsConfirmMap = {
                                                    'Confirmed': { label: 'Confirmed (DaySmart)', icon: '✅', bg: 'bg-green-100', text: 'text-green-700' },
                                                    'Talked To': { label: 'Talked To (DaySmart)', icon: '🗣️', bg: 'bg-blue-100', text: 'text-blue-700' },
                                                    'Left Voicemail': { label: 'Voicemail (DaySmart)', icon: '🎤', bg: 'bg-yellow-100', text: 'text-yellow-700' },
                                                    'Left Message': { label: 'Left Message (DaySmart)', icon: '🎤', bg: 'bg-yellow-100', text: 'text-yellow-700' },
                                                    'Emailed': { label: 'Emailed (DaySmart)', icon: '📧', bg: 'bg-blue-100', text: 'text-blue-700' },
                                                    'Texted': { label: 'Texted (DaySmart)', icon: '📱', bg: 'bg-blue-100', text: 'text-blue-700' },
                                                };
                                                return dsConfirmMap[appt.confirmation_type] || { 
                                                    label: `${appt.confirmation_type} (DaySmart)`, icon: '📋', bg: 'bg-teal-100', text: 'text-teal-700' 
                                                };
                                            }
                                            return { label: 'Not reminded', icon: '⏳', bg: 'bg-gray-100', text: 'text-gray-600' };
                                        }
                                        
                                        // Get the most recent meaningful event
                                        const events = chain.events.sort((a, b) => 
                                            new Date(b.timestamp) - new Date(a.timestamp)
                                        );
                                        const lastEvent = events[0];
                                        const eventType = lastEvent?.type;
                                        
                                        // Map event types to display status
                                        const statusMap = {
                                            'reminder_confirmed': { label: 'Confirmed', icon: '✅', bg: 'bg-green-100', text: 'text-green-700' },
                                            'reminder_declined_cancelled': { label: 'Cancelled', icon: '❌', bg: 'bg-red-100', text: 'text-red-700' },
                                            'reminder_declined_reschedule_requested': { label: 'Reschedule Requested', icon: '📅', bg: 'bg-orange-100', text: 'text-orange-700' },
                                            'reminder_declined_rescheduled': { label: 'Rescheduled', icon: '🔄', bg: 'bg-green-100', text: 'text-green-700' },
                                            'reminder_voicemail': { label: 'Voicemail Left', icon: '🎤', bg: 'bg-yellow-100', text: 'text-yellow-700' },
                                            'reminder_spoke_to': { label: 'Spoke To', icon: '🗣️', bg: 'bg-blue-100', text: 'text-blue-700' },
                                            'reminder_text': { label: 'Text Sent', icon: '📱', bg: 'bg-blue-100', text: 'text-blue-700' },
                                            'outbound_call': { label: 'Called', icon: '📞', bg: 'bg-blue-100', text: 'text-blue-700' },
                                        };
                                        
                                        return statusMap[eventType] || { label: 'In Progress', icon: '🔄', bg: 'bg-blue-100', text: 'text-blue-700' };
                                    };
                                    
                                    const status = getChainStatus();
                                    
                                    // Get call time from chain
                                    const getCallTime = () => {
                                        if (!chain || !chain.events) return null;
                                        const callEvent = chain.events.find(e => 
                                            e.type === 'outbound_call' || 
                                            e.type?.startsWith('reminder_')
                                        );
                                        if (callEvent?.timestamp) {
                                            return new Date(callEvent.timestamp).toLocaleTimeString('en-US', {
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true
                                            });
                                        }
                                        return null;
                                    };
                                    
                                    const callTime = getCallTime();
                                    
                                    return (
                                        <div 
                                            key={idx} 
                                            className={`bg-white rounded-lg shadow overflow-hidden transition-all ${
                                                isSelected ? 'ring-2 ring-indigo-500' : ''
                                            }`}
                                        >
                                            <div className="p-4">
                                                <div className="flex items-start gap-3">
                                                    {/* Checkbox */}
                                                    {hasPhone && (
                                                        <div className="pt-1">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleAppointmentSelection(apptKey)}
                                                                disabled={groombotCalling}
                                                                className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:opacity-50"
                                                            />
                                                        </div>
                                                    )}
                                                    {!hasPhone && (
                                                        <div className="pt-1 w-5" title="No phone number">
                                                            <PhoneOff size={18} className="text-gray-300" />
                                                        </div>
                                                    )}
                                                    
                                                    {/* Appointment Info */}
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <span className="text-sm font-medium text-gray-600">
                                                                {formatTime(appt.start_time)}
                                                            </span>
                                                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">
                                                                {appt.location_id === 1 ? 'Hopkins' : 'Inver Grove'}
                                                            </span>
                                                        </div>
                                                        <h4 className="font-semibold text-gray-900">
                                                            {appt.client_name || 'Unknown Client'}
                                                        </h4>
                                                        <div className="text-sm text-gray-600 mt-1">
                                                            {appt.pet_name && <span className="mr-3">🐾 {appt.pet_name}</span>}
                                                            {appt.service_name && <span>{appt.service_name}</span>}
                                                        </div>
                                                        {appt.employee_name && (
                                                            <div className="text-sm text-gray-500 mt-1">
                                                                with {appt.employee_name}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Status Badge with Call Time */}
                                                    <div className="text-right">
                                                        <div className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${status.bg} ${status.text}`}>
                                                            {status.icon} {status.label}
                                                        </div>
                                                        {callTime && (
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                Called at {callTime}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Communication Chain Details */}
                                                {chain && chain.events && chain.events.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t">
                                                        <div className="text-xs text-gray-500 mb-2">Communication Chain:</div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {getEventsNewestFirst(chain.events)
                                                                .map((event, eventIdx) => {
                                                                    const eventInfo = getEventTypeInfo(event.type);
                                                                    const eventTime = new Date(event.timestamp).toLocaleTimeString('en-US', {
                                                                        hour: 'numeric',
                                                                        minute: '2-digit',
                                                                        hour12: true
                                                                    });
                                                                    return (
                                                                        <div 
                                                                            key={eventIdx}
                                                                            className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded text-xs"
                                                                            title={event.notes || eventInfo.label}
                                                                        >
                                                                            <span>{eventInfo.icon}</span>
                                                                            <span className={eventInfo.color}>{eventInfo.label}</span>
                                                                            <span className="text-gray-400">@ {eventTime}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                        {/* Cancellation Fee Status */}
                                                        {chain.cancellation_fee_status && chain.cancellation_fee_status !== 'not_applicable' && (
                                                            <div className="mt-2">
                                                                <span className={`text-xs px-2 py-1 rounded ${
                                                                    chain.cancellation_fee_status === 'cancellation_fee_paid' 
                                                                        ? 'bg-green-100 text-green-700'
                                                                        : chain.cancellation_fee_status === 'cancellation_fee_waived'
                                                                        ? 'bg-blue-100 text-blue-700'
                                                                        : 'bg-red-100 text-red-700'
                                                                }`}>
                                                                    💰 {chain.cancellation_fee_status === 'cancellation_fee_paid' 
                                                                        ? 'Fee Paid' 
                                                                        : chain.cancellation_fee_status === 'cancellation_fee_waived'
                                                                        ? 'Fee Waived'
                                                                        : 'Fee Owed (50%)'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {/* Manual Call Button */}
                                                {appt.client_cell_phone && (
                                                    <div className="mt-3 pt-3 border-t flex items-center gap-3">
                                                        <button
                                                            onClick={() => handleCallClient(appt.client_cell_phone, {
                                                                clientId: appt.client_id,
                                                                clientName: appt.client_name || 'Unknown Client',
                                                                locationId: appt.location_id,
                                                                eventNotes: `Reminder manual call for ${reminderDate}`,
                                                                appointmentId: appt.appointment_id,
                                                                appointmentDate: reminderDate,
                                                            })}
                                                            className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                                                        >
                                                            <PhoneOutgoing size={14} />
                                                            Call Manually
                                                        </button>
                                                        <span className="text-xs text-gray-500">
                                                            {formatPhoneNumber(appt.client_cell_phone)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Unmatched Chains - chains that don't correspond to any appointment */}
                                {(() => {
                                    const matchedKeys = new Set(reminderData.map(a => `${a.location_id}-${a.appointment_id}`));
                                    const unmatchedChains = Object.entries(reminderChains)
                                        .filter(([key]) => !matchedKeys.has(key))
                                        .map(([, chain]) => chain);
                                    if (unmatchedChains.length === 0) return null;
                                    return (
                                        <>
                                            <div className="mt-6 mb-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                                Other Reminder Chains (no matching appointment)
                                            </div>
                                            {unmatchedChains.map((chain, idx) => {
                                                const lastEvent = getEventsNewestFirst(chain.events || [])[0];
                                                const statusMap = {
                                                    'reminder_confirmed': { label: 'Confirmed', icon: '✅', bg: 'bg-green-100', text: 'text-green-700' },
                                                    'reminder_declined_cancelled': { label: 'Cancelled', icon: '❌', bg: 'bg-red-100', text: 'text-red-700' },
                                                    'reminder_declined_reschedule_requested': { label: 'Reschedule Requested', icon: '📅', bg: 'bg-orange-100', text: 'text-orange-700' },
                                                    'reminder_declined_rescheduled': { label: 'Rescheduled', icon: '🔄', bg: 'bg-green-100', text: 'text-green-700' },
                                                    'reminder_voicemail': { label: 'Voicemail Left', icon: '🎤', bg: 'bg-yellow-100', text: 'text-yellow-700' },
                                                    'reminder_spoke_to': { label: 'Spoke To', icon: '🗣️', bg: 'bg-blue-100', text: 'text-blue-700' },
                                                    'outbound_call': { label: 'Called', icon: '📞', bg: 'bg-blue-100', text: 'text-blue-700' },
                                                };
                                                const status = (lastEvent && statusMap[lastEvent.type]) || { label: chain.status || 'Open', icon: '🔗', bg: 'bg-gray-100', text: 'text-gray-600' };
                                                return (
                                                    <div key={`orphan-${idx}`} className="bg-white rounded-lg shadow overflow-hidden border-l-4 border-gray-300">
                                                        <div className="p-4">
                                                            <div className="flex items-start gap-3">
                                                                <div className="flex-1">
                                                                    <h4 className="font-semibold text-gray-900">
                                                                        {chain.client_name || 'Unknown Client'}
                                                                    </h4>
                                                                    <div className="text-sm text-gray-500 mt-1">
                                                                        {chain.client_phone && <span className="mr-3">📞 {formatPhoneNumber(chain.client_phone)}</span>}
                                                                        <span className="text-xs text-gray-400">Appt ID: {chain.appointment_id}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="text-right">
                                                                    <div className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${status.bg} ${status.text}`}>
                                                                        {status.icon} {status.label}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {chain.events && chain.events.length > 0 && (
                                                                <div className="mt-3 pt-3 border-t">
                                                                    <div className="text-xs text-gray-500 mb-2">Communication Chain:</div>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {getEventsNewestFirst(chain.events)
                                                                            .map((event, eventIdx) => {
                                                                                const eventInfo = getEventTypeInfo(event.type);
                                                                                const eventTime = new Date(event.timestamp).toLocaleTimeString('en-US', {
                                                                                    hour: 'numeric', minute: '2-digit', hour12: true
                                                                                });
                                                                                return (
                                                                                    <div key={eventIdx} className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded text-xs" title={event.notes || eventInfo.label}>
                                                                                        <span>{eventInfo.icon}</span>
                                                                                        <span className={eventInfo.color}>{eventInfo.label}</span>
                                                                                        <span className="text-gray-400">@ {eventTime}</span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Empty State */}
                        {!reminderLoading && reminderData.length === 0 && (
                            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                                No appointments found for the selected date.
                            </div>
                        )}
                    </div>
                )}

                {/* Availability Tab */}
                {activeTab === 'availability' && (
                    <AvailabilityPanel />
                )}
            </div>

            {/* Appointment Details Modal */}
            <AppointmentDetailsModal
                isOpen={showAppointmentModal}
                onClose={handleCloseAppointmentModal}
                ticket={ticketDetails}
                loading={ticketLoading}
                error={ticketError}
                onPaymentComplete={handlePaymentComplete}
                currentUserId={currentUser?.user_id}
                viewOnly={modalViewOnly}
                onCallClient={handleCallClient}
            />

            {/* Groomer Availability Modal */}
            <GroomerAvailabilityModal
                isOpen={showGroomerModal}
                onClose={() => {
                    setShowGroomerModal(false);
                    setGroomerModalEmployee(null);
                    setGroomerModalLocationId(null);
                }}
                employee={groomerModalEmployee}
                locationId={groomerModalLocationId}
            />
        </div>
    );
};

export default DaySmartDashboard;
